import { BITSTRING_LENGTH, SELECTION_TIME_INTERVAL, TIME_INTERVAL } from "../constants";
import { Appointment, BitStringData, Employee, RecurringBreak, occupiesSchedule } from "../types";
import { Dayjs, dayjs } from "../utils/dayjs-config";

// Helper function to convert time in "HHMM" military format to minutes from 00:00
export const timeToMinutes = (time: string): number => {
  const hours = parseInt(time.slice(0, 2), 10);
  const minutes = parseInt(time.slice(2), 10);
  return hours * 60 + minutes;
};

export const parseTime = (timeStr: string): Dayjs | null => {
  if (!timeStr) return null;
  const hours = parseInt(timeStr.slice(0, 2), 10);
  const minutes = parseInt(timeStr.slice(2), 10);
  return dayjs.tz().set('hour', hours).set('minute', minutes);
};


// 1. Get bit representation for a certain time in military format
export const getBitRepresentation = (time: string, interval = TIME_INTERVAL): string => {
  const bitPosition = Math.floor(timeToMinutes(time) / interval);
  return '0'.repeat(bitPosition) + '1' + '0'.repeat(BITSTRING_LENGTH - bitPosition - 1);
};

// 2. Given two times (in military format), return a bit string with that time as occupied
export const getOccupiedBitString = (startTime: string, endTime: string, interval = TIME_INTERVAL): string => {
  const startBit = Math.floor(timeToMinutes(startTime) / interval);
  const endBit = Math.floor(timeToMinutes(endTime) / interval);

  return '0'.repeat(startBit) + '1'.repeat(endBit - startBit) + '0'.repeat(BITSTRING_LENGTH - endBit);
};

// Function to convert 24-hour time to "HHMM" format
export const convert12HourTo24Hour = (time: string): string => {
  const [timePart, period] = time.split(' ');
  const [hours, minutes] = timePart.split(':').map(Number);
  const hours24 = period === 'PM' ? (hours % 12) + 12 : hours % 12;
  return `${String(hours24).padStart(2, '0')}${String(minutes).padStart(2, '0')}`;
};

// Helper functions for formatting remain unchanged
export const convertTo24HourString = (time: Dayjs | null): string => {
  if (!time) return '';
  const hours = time.hour();
  const minutes = time.minute();
  return `${String(hours).padStart(2, '0')}${String(minutes).padStart(2, '0')}`;
};

/*Display time as '9:00AM'
Input params: 0900
*/
export const formatTime = (time: string) => {
  const hours = parseInt(time.slice(0, 2), 10);
  const minutes = time.slice(2);
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const hour12 = hours % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};

/*Display time range as '9:00AM - 9:30AM'
Input params: 0900,30
*/
export const formatTimeRange = (startTime: string, serviceTime: number) => {
  const startHours = parseInt(startTime.slice(0, 2), 10);
  const startMinutes = parseInt(startTime.slice(2), 10);
  const endMinutes = startMinutes + serviceTime;
  const endHours = startHours + Math.floor(endMinutes / 60);
  const formattedEndMinutes = endMinutes % 60;

  return `${formatTime(startTime)} - ${formatTime(`${endHours.toString().padStart(2, '0')}${formattedEndMinutes.toString().padStart(2, '0')}`)}`;
};

// Turn minutes into a human-readable format
export const formatMinutes = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  const hoursText = hours > 0 ? `${hours} hr${hours > 1 ? 's' : ''}` : '';
  const minutesText = remainingMinutes > 0 ? `${remainingMinutes} min${remainingMinutes > 1 ? 's' : ''}` : '';
  return [hoursText, minutesText].filter(Boolean).join(' ');
};

export const convertTo12Hour = (militaryTime: string): string => {
  // Extract the hours and minutes from the military time
  const hours24 = parseInt(militaryTime.slice(0, 2), 10);
  const minutes = militaryTime.slice(2);

  // Determine if it's AM or PM
  const period = hours24 >= 12 ? 'PM' : 'AM';

  // Convert hours to 12-hour format
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;

  // Return the formatted time in "HH:MM AM/PM"
  return `${hours12}:${minutes} ${period}`;
};

/*Display date as 'Sat, Sept 21, 2024'
Input params: MMDDYYYY
*/
export const formatDate = (dateString: string) => {
  const [month, day, year] = [dateString.slice(0, 2), dateString.slice(2, 4), dateString.slice(4)];
  const date = new Date(`${month}/${day}/${year}`);

  // Days of the week
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  // Months
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  return `${days[date.getDay()]}, ${months[date.getMonth()]} ${parseInt(day, 10)}, ${year}`;
};

export const parseDateFromMMDDYYYY = (dateString: string): Dayjs => {
  return dayjs(dateString, 'MMDDYYYY');
};

export const formatDateToISO = (date: Dayjs): string => date.format('YYYY-MM-DD');

export const parseDateFromISO = (dateString: string): Dayjs => dayjs(dateString, 'YYYY-MM-DD');

export const getDateWithSuffix = (date: Dayjs) => {
  const day = date.date();
  const suffix = day === 1 || day === 21 || day === 31 ? 'st' : day === 2 || day === 22 ? 'nd' : day === 3 || day === 23 ? 'rd' : 'th';
  return `${day}${suffix}`;
};


// Function to generate a bit string between two times based on the fill parameter
// Basically gets a work hours
export const getBitStringBetweenTimes = (
  startTime: string,
  endTime: string,
  fill: boolean, // true if booking, false if freeing up
  interval = TIME_INTERVAL,
): string => {
  const startBit = Math.floor(timeToMinutes(startTime) / interval);
  const endBit = Math.floor(timeToMinutes(endTime) / interval);

  const fillChar = fill ? '1' : '0'; // Determines whether to fill with 1s or 0s
  const opposite = fill ? '0' : '1';

  // Create the bit string: '0's before start, fill between, and '0's after end
  let finalBitMap = (opposite.repeat(startBit) + fillChar.repeat(endBit - startBit) + opposite.repeat(BITSTRING_LENGTH - endBit))
  return finalBitMap;
};

// This will fill in the bits (based on the fill bool), from the start to the end bit.
export const fillRangeInBitString = (
  bitString: string,
  startTime: string,
  endTime: string,
  fill: boolean, // true = 1s false =0s 
  interval = TIME_INTERVAL,
): string => {
  const bitLength = bitString.length;
  const startBit = Math.floor((timeToMinutes(startTime)) / interval);
  const endBit = Math.floor((timeToMinutes(endTime)) / interval);

  const fillChar = fill ? '1' : '0'; // Determines whether to fill with 1s or 0s

  // Ensure the start and end positions are within the bit string bounds
  const clampedStartBit = Math.max(0, Math.min(startBit, bitLength));
  const clampedEndBit = Math.max(clampedStartBit, Math.min(endBit, bitLength));

  // Create the new bit string with the specified range filled
  const newBitString =
    bitString.slice(0, clampedStartBit) +
    fillChar.repeat(clampedEndBit - clampedStartBit) +
    bitString.slice(clampedEndBit);

  return newBitString;
};

// 3. Get the appointment time for a service (in military format)
export const getServiceAppointmentBit = (startTime: string, serviceDuration: number, interval = TIME_INTERVAL): string => {
  const startBit = Math.floor(timeToMinutes(startTime) / interval);
  const serviceBlocks = Math.ceil(serviceDuration / interval);
  const endBit = startBit + serviceBlocks;

  return '0'.repeat(startBit) + '1'.repeat(serviceBlocks) + '0'.repeat(BITSTRING_LENGTH - endBit);
};

export const generateNineToFiveBitString = () => {
  const slotsPerHour = 60 / TIME_INTERVAL;

  // Total slots for 9 AM - 5 PM
  const startIndex = 9 * slotsPerHour;      // 9:00 AM
  const lunchStartIndex = 12 * slotsPerHour; // 12:00 PM (Lunch Break)
  const lunchEndIndex = 13 * slotsPerHour;   // 1:00 PM
  const endIndex = 17 * slotsPerHour;        // 5:00 PM

  const bitstring = Array(BITSTRING_LENGTH).fill('0'); // Default to unavailable

  // Fill 9 AM - 12 PM slots
  bitstring.fill('1', startIndex, lunchStartIndex);
  // Fill 1 PM - 5 PM slots
  bitstring.fill('1', lunchEndIndex, endIndex);

  return bitstring.join('');
};


// 4. Find free slots in schedule.time for the service, return the free times in military format (HHMM)
export const findFreeSlots = (
  scheduleTime: string,
  serviceDuration: number,
  disablePastTimeToday: boolean = false,
  interval = TIME_INTERVAL
): string[] => {
  const serviceBlocks = Math.ceil(serviceDuration / interval);
  const freeSlots: string[] = [];
  const maxIndex = BITSTRING_LENGTH - serviceBlocks; // Last index where service can fit

  // Get the current time if we need to disable past times
  const now = disablePastTimeToday ? dayjs.tz() : null;
  const currentTimeInMinutes = now ? now.hour() * 60 + now.minute() : 0;

  const selectionStep = Math.floor(SELECTION_TIME_INTERVAL / interval); // This determines how time intervals for the selection are stepped

  // Iterate through possible starting indices for the service, based on the selection step
  for (let i = 0; i <= maxIndex; i += selectionStep) {
    const currentBlock = scheduleTime.slice(i, i + serviceBlocks);

    if (currentBlock === '0'.repeat(serviceBlocks)) {
      const slotStartMinutes = i * interval;

      // If disablePastTimeToday is true, filter out past times for today
      if (disablePastTimeToday && slotStartMinutes < currentTimeInMinutes) {
        continue; // Skip this time slot if it is in the past
      }

      freeSlots.push(i.toString());
    }
  }

  // Convert slot indices to "HHMM" format
  return freeSlots.map((slot) => {
    const slotStartMinutes = parseInt(slot, 10) * interval;
    const hours = Math.floor(slotStartMinutes / 60).toString().padStart(2, '0');
    const minutes = (slotStartMinutes % 60).toString().padStart(2, '0');
    return `${hours}${minutes}`; // Return in "HHMM" format
  });
};

// We use this to filter out slots that do not meet the minimum notice 
// For example if it's 3PM now and minimum notice is 2 hours, we filter out slots before 5PM
export const filterSlotsByMinimumNoticeThreshold = (
  slots: string[],
  date: Dayjs,
  earliestDateTime?: Dayjs | null
): string[] => {
  if (!earliestDateTime) return slots;

  return slots.filter((slot) => {
    const hours = parseInt(slot.slice(0, 2), 10);
    const minutes = parseInt(slot.slice(2), 10);
    const slotDateTime = date.hour(hours).minute(minutes).second(0).millisecond(0);
    return !slotDateTime.isBefore(earliestDateTime);
  });
};

export const dayHasFreeSlots = (
  scheduleTime: string,
  serviceDuration: number,
  interval = TIME_INTERVAL
): boolean => {
  const serviceBlocks = Math.ceil(serviceDuration / interval);
  const maxIndex = BITSTRING_LENGTH - serviceBlocks; // Last index where service can fit

  for (let i = 0; i <= maxIndex; i++) {
    const currentBlock = scheduleTime.slice(i, i + serviceBlocks);

    if (currentBlock === '0'.repeat(serviceBlocks)) {
      return true;
    }
  }
  return false;
};


export const formatDateToMMDDYYYY = (date: Dayjs): string => {
  const month = String(date.month() + 1).padStart(2, '0');
  const day = String(date.date()).padStart(2, '0');
  const year = date.year();
  return `${month}${day}${year}`;
};

// Function to iterate between two dates
export const getDatesInRange = (startDate: Dayjs, endDate: Dayjs): Dayjs[] => {
  const dates = [];
  let currentDate = dayjs(startDate);

  while (currentDate <= endDate) {
    dates.push(currentDate.clone()); // Helper function formats date to MMDDYYYY
    currentDate = currentDate.add(1, 'day');
  }

  return dates;
};


export const generateAllOnesBitString = (): string => '1'.repeat(BITSTRING_LENGTH);

// Generate a bitstring given a BitStringData
export const generateScheduleBitString = (
  scheduleData: BitStringData
): string => {
  if (scheduleData.isDayOff) return generateAllOnesBitString();

  let startTime = scheduleData.startTime.format('HHmm');
  let endTime = scheduleData.endTime.format('HHmm');

  return getBitStringBetweenTimes(startTime, endTime, false);
};

// Extracts an object of data from a bitstring with all needed info.
export const extractTimesFromBitString = (bitString: string): BitStringData => {
  // Default BitStringData assuming it's a day off
  const defaultStartTime = dayjs().hour(9).minute(0); // 09:00 AM
  const defaultEndTime = dayjs().hour(17).minute(0); // 05:00 PM

  const bitStringData: BitStringData = {
    bitString: bitString,
    startTime: defaultStartTime,
    endTime: defaultEndTime,
    isDayOff: true,
  };

  // Check if it's a day off (all '1's in the bitString)
  if (!bitString.includes('0')) return bitStringData;

  // Find start and end of available time
  const startBit = bitString.indexOf('0');
  const endBit = bitString.lastIndexOf('0');

  // Helper to convert bit index to a Dayjs object representing a BITSTRING_LENGTH-minute interval time
  const convertBitToTime = (bitIndex: number): Dayjs => {
    const totalMinutes = bitIndex * TIME_INTERVAL;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return dayjs().hour(hours).minute(minutes);
  };

  // Construct final BitStringData object based on parsed values
  return {
    bitString: bitString,
    startTime: convertBitToTime(startBit),
    endTime: convertBitToTime(endBit + 1),
    isDayOff: false,
  };
};


/**
 * Checks if the given time range is free within a bit string schedule.
 * @param {string} bitString - A BITSTRING_LENGTH-bit string representing the day's schedule.
 * @param {number} startTime - Start time in minutes.
 * @param {number} serviceTime - time in minutes to check.
 * @returns {boolean} - True if the entire range is free; false otherwise.
 */
export const isTimeFree = (bitString: string, startTime: number, serviceTime: number = TIME_INTERVAL): boolean => {
  const startIndex = Math.floor(startTime / TIME_INTERVAL);
  const endIndex = startIndex + (serviceTime / TIME_INTERVAL);
  // Iterate from start index to end index and check each bit
  for (let i = startIndex; i < endIndex; i++) {
    if (bitString[i] === '1') {
      return false; // If any bit is occupied, time is not free
    }
  }
  return true; // All bits in the range are '0', so time is free
};

// Given a list of appointments, and the schedule we can get the real bitstring.
export const extractWorkHours = (
  appointments: Array<Appointment>,
  bitString: string
): BitStringData => {
  const schedulableAppointments = appointments.filter(occupiesSchedule);
  const defaultStartTime = dayjs().hour(9).minute(0); // 09:00 AM
  const defaultEndTime = dayjs().hour(17).minute(0); // 05:00 PM

  // Initialize the BitStringData object
  const bitStringData: BitStringData = {
    bitString, // Start with the provided bit string
    startTime: defaultStartTime,
    endTime: defaultEndTime,
    isDayOff: true, // Default assumption
  };

  if (bitString === generateAllOnesBitString() && schedulableAppointments.length === 0) {
    return bitStringData;
  } else {
    bitStringData.isDayOff = false;
  }

  // Remove the '1's corresponding to each appointment from the bitString
  schedulableAppointments.forEach((appointment) => {

    const startTime = dayjs(appointment.appointmentTime, "HHmm");
    const endTime = startTime.add(appointment.appointmentDuration, "minute");

    // Fill the appointment slots with '0' (freeing them)
    const updatedBitString = fillRangeInBitString(
      bitStringData.bitString,
      appointment.appointmentTime,
      endTime.format("HHmm"),
      false // Free up the bits by setting them to '0'
    );

    bitStringData.bitString = updatedBitString;
  });

  // Update other properties of BitStringData
  const updatedBitStringData = extractTimesFromBitString(bitStringData.bitString);

  // Merge updated properties
  const finalBitStringData = {
    ...bitStringData,
    ...updatedBitStringData,
  };

  return finalBitStringData;
};

export const isDateBetween = (date: Dayjs, startDate: Dayjs, endDate: Dayjs, inclusive: boolean = true) => {
  if (inclusive) {
    return date.isAfter(startDate.subtract(1, "day")) && date.isBefore(endDate.add(1, "day"));
  }
  return date.isAfter(startDate) && date.isBefore(endDate);
};

// Takes in a bitstring and updates it accordinlgy to the selected time and serviceDuration
export const updateScheduleBitString = (
  bitString: string,
  selectedTime: string,
  appointmentDuration: number,
  cancel: boolean
): string | null => {
  const startMinutes = timeToMinutes(selectedTime);
  const startIndex = Math.floor(startMinutes / TIME_INTERVAL);
  const serviceBlocks = Math.ceil(appointmentDuration / TIME_INTERVAL);
  const endIndex = startIndex + serviceBlocks;

  for (let i = startIndex; i < endIndex; i++) {
    const bit = bitString[i];
    if (cancel) {
      if (bit !== '1') {
        // If canceling, we expect '1' (booked) bits to set back to '0' (free)
        console.warn(`Cancel failed: Expected bit at index ${i} to be '1', found '${bit}'`);
        return null;
      }
    } else {
      if (bit !== '0') {
        // If booking, we expect '0' (free) bits to set to '1' (booked)
        console.warn(`Booking failed: Expected bit at index ${i} to be '0', found '${bit}'`);
        return null;
      }
    }
  }

  // Update the bit string: set bits to '1' for booking, '0' for canceling
  const updatedBitString = bitString.split('');
  for (let i = startIndex; i < endIndex; i++) {
    updatedBitString[i] = cancel ? '0' : '1';
  }

  const finalString = updatedBitString.join('');
  return finalString;
};

export const roundToNearestInterval = (time: Dayjs): Dayjs => {
  const minutes = time.minute();
  const roundedMinutes = Math.round(minutes / TIME_INTERVAL) * TIME_INTERVAL;
  return time.startOf('hour').add(roundedMinutes, 'minute');
};

export const isAppointmentEndTimePast = (appointment: Appointment): boolean => {
  const now = dayjs.tz();
  const appointmentStartTime = dayjs(appointment.appointmentTime, 'HHmm');
  const appointmentDate = dayjs(appointment.appointmentDate, 'MMDDYYYY')
    .hour(appointmentStartTime.hour())
    .minute(appointmentStartTime.minute());
  const appointmentEnd = appointmentDate.add(appointment.appointmentDuration, 'minute');
  return appointmentEnd.isBefore(now);
}

export const isAppointmentStartTimePast = (appointment: Appointment): boolean => {
  const now = dayjs.tz();
  const appointmentStartTime = dayjs(appointment.appointmentTime, 'HHmm');
  const appointmentDate = dayjs(appointment.appointmentDate, 'MMDDYYYY')
    .hour(appointmentStartTime.hour())
    .minute(appointmentStartTime.minute());
  return appointmentDate.isBefore(now);
}

export interface RecurringBreakInstance {
  definitionId: string;
  start: Dayjs;
  end: Dayjs;
}

// Clamps a break instance to a given window, returns false if completely outside
const clampBreakWindow = (instance: RecurringBreakInstance, windowStart: Dayjs, windowEnd: Dayjs): boolean => {
  if (instance.end.isBefore(windowStart) || instance.start.isAfter(windowEnd)) {
    return false;
  }
  return true;
};

// Parses a time string in "HHMM" format to a Dayjs object on the given base date
const parseBreakTime = (base: Dayjs, timeStr: string): Dayjs => {
  const hours = parseInt(timeStr.slice(0, 2), 10);
  const minutes = parseInt(timeStr.slice(2), 10);
  return base.hour(hours).minute(minutes).second(0).millisecond(0);
};

// Generates instances of recurring breaks within a specified date window
// Steps are
// 1. Filter out invalid definitions
// 2. For each definition, generate instances within the window
// 3. Sort instances by start time
export const getRecurringBreakInstances = (
  definitions: RecurringBreak[] | undefined,
  windowStart: Dayjs,
  windowEnd: Dayjs
): RecurringBreakInstance[] => {
  if (!definitions || definitions.length === 0) return [];
  if (!windowStart || !windowEnd || windowEnd.isBefore(windowStart)) return [];

  const sortedDefs = [...definitions].filter((def) => !!def.id && !!def.startTime && !!def.endTime);
  if (sortedDefs.length === 0) {
    return [];
  }

  const windowStartDay = windowStart.startOf('day');
  const windowEndDay = windowEnd.endOf('day');
  const anchor = windowStartDay.startOf('week');
  const windowInstances: RecurringBreakInstance[] = [];

  for (const def of sortedDefs) {
    const repeatInterval = Math.max(1, Math.min(4, def.repeatEveryXWeeks || 1));
    const parsedRepeatUntil = def.indefinite || !def.repeatUntilDate
      ? null
      : dayjs(def.repeatUntilDate);
    const repeatUntil = parsedRepeatUntil && parsedRepeatUntil.isValid()
      ? parsedRepeatUntil.endOf('day')
      : null;
    const recurrenceEnd = repeatUntil && repeatUntil.isBefore(windowEndDay)
      ? repeatUntil
      : windowEndDay;
    if (recurrenceEnd.isBefore(windowStartDay, 'day')) {
      continue;
    }

    const startDayOfWeek = windowStartDay.day();
    const dayOffset = (def.weekday - startDayOfWeek + 7) % 7;
    let candidate = windowStartDay.add(dayOffset, 'day');

    // Align the candidate to the repeat cadence relative to the anchor week.
    const daysFromAnchor = candidate.startOf('day').diff(anchor, 'day');
    let weeksFromAnchor = Math.floor(daysFromAnchor / 7);
    if (weeksFromAnchor < 0) {
      weeksFromAnchor = 0;
    }
    const remainder = weeksFromAnchor % repeatInterval;
    if (remainder !== 0) {
      const weeksToAdd = repeatInterval - remainder;
      candidate = candidate.add(weeksToAdd, 'week');
    }

    while (!candidate.isAfter(recurrenceEnd, 'day')) {
      const start = parseBreakTime(candidate.clone(), def.startTime);
      const end = parseBreakTime(candidate.clone(), def.endTime);
      const durationMinutes = end.diff(start, 'minute');
      if (durationMinutes > 0) {
        const instance = { definitionId: def.id, start, end };
        if (clampBreakWindow(instance, windowStartDay, windowEndDay)) {
          windowInstances.push(instance);
        }
      }
      candidate = candidate.add(repeatInterval, 'week');
    }
  }

  return windowInstances.sort((a, b) => a.start.valueOf() - b.start.valueOf());
};

export const getAppointmentEndTime = (startTime: string, durationMinutes: number): string => {
  const startMinutes = timeToMinutes(startTime);
  const totalMinutes = startMinutes + durationMinutes;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, '0')}${String(minutes).padStart(2, '0')}`;
};


export const collectAppointmentIdsWithinWindow = (
  appointmentMap: Employee['appointments'] = {},
  windowStart: Dayjs,
  windowEnd: Dayjs
): Set<string> => {
  const ids = new Set<string>();
  Object.entries(appointmentMap).forEach(([dateKey, values]) => {
    if (!Array.isArray(values) || values.length === 0) {
      return;
    }
    const appointmentDate = parseDateFromMMDDYYYY(dateKey).startOf('day');
    if (appointmentDate.isBefore(windowStart) || appointmentDate.isAfter(windowEnd)) {
      return;
    }
    values.forEach((id) => {
      if (typeof id === 'string' && id.trim().length > 0) {
        ids.add(id);
      }
    });
  });
  return ids;
};
